import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { initializeVersionManager } from './commons/versionManager';
import { initializeSoundLeader } from './commons/notificationSound';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router';
import { Provider } from 'react-redux';
import { store } from './store';
import { LocaleProvider } from './contexts/LocaleContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import {
  createTheme,
  MantineProvider,
  localStorageColorSchemeManager,
  LoadingOverlay,
  Box,
  Center,
  Container,
} from '@mantine/core';

import { Notifications } from '@mantine/notifications';
import { CreateModel } from './users/pages/UserModels/CreateModel';
import { EditModel } from './users/pages/UserModels/EditModel';
import { UserModels } from './users/pages/UserModels';

import {
  CreateFilter,
  EditFilter,
  UserFilters,
  UserFilter,
} from './users/pages/UserFilters';
import { UserQueryFoldersPage } from './users/pages/UserQueryFolders';

import { Playground } from './pages/Playground';

import { UserDashboard } from './users/pages/UserDashboard';
import { UserUploads } from './users/pages/UserUploads';
import { UserSettings } from './users/pages/UserSettings';
import { UserNotifications } from './users/pages/UserNotifications';
import { UsersMenu } from './users/components/UsersMenu';

import routes from './commons/routes';

import './index.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { UserDocumentCreate } from './users/pages/UserDocuments';
import { UserProvider } from './contexts/UserContext';
import { UserLogin } from './users/pages/UserLogin';
import { UserRegister } from './users/pages/UserRegister/UserRegister';
import { ClientLogin } from './clients/pages/ClientLogin/ClientLogin';
import { ClientProvider } from './contexts/ClientContext';
import { Root } from './pages/Root';
import { FolderPage } from './clients/pages/FolderPage';
import { FileEditPage } from './clients/pages/FileEditPage';
import { LogoSVG } from '@components/Logo/Logo';
import { useTheme } from './components/ThemeSwitch';
import { EmptyStatePage } from './components/EmptyStatePage';
import { RequireUser } from './users/components/RequireUser';
import { EditFileStructure } from './users/components/UserFolders/EditFileStructure';
import { RedirectOldClientFolder } from './users/components/RedirectOldClientFolder';
import { LocalStorageManager } from './components/LocalStorageManager';
import { ModelsInitializer } from './components/ModelsInitializer';
import { DashboardInitializer } from './components/DashboardInitializer';
import { FilesInitializer } from './components/FilesInitializer';
import { UploadsInitializer } from './components/UploadsInitializer';
import { UserFoldersInitializer } from './components/UserFoldersInitializer';
import { SettingsInitializer } from './components/SettingsInitializer';
import { NotificationsInitializer } from './components/NotificationsInitializer';
import { VersionChangeNotifier } from './components/VersionChangeNotifier';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { useCrossTabSync } from './hooks/useCrossTabSync';
import { FiltersInitializer } from './components/FiltersInitializer';

import * as Sentry from '@sentry/react';

process.env.NODE_ENV === 'production' &&
  Sentry.init({
    dsn: 'https://cf42ae6229cd21a9aae38ddd77ca33d4@o4510479479078912.ingest.de.sentry.io/4510479480193104',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

const theme = createTheme({});

const colorSchemeManager = localStorageColorSchemeManager({
  key: 'absolvire-app-theme',
});

function AppContent() {
  const { isLoading } = useLoading();
  const { computedColorScheme } = useTheme();

  // Enable cross-tab sync for Redux state
  useCrossTabSync();

  return (
    <Container mih="100svh" maw="100svw" p={0} m={0}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={9999}
        overlayProps={{ radius: 'sm', blur: 2, pos: 'fixed' }}
        loaderProps={{
          pos: 'fixed',
          top: '50%',
          left: '50%',
          style: { transform: 'translate(-50%, -50%)' },
        }}
      />
      <LocalStorageManager />
      <Routes>
        <Route
          element={
            <UserProvider>
              <Outlet />
            </UserProvider>
          }
        >
          <Route path={routes.routes.userRegister} element={<UserRegister />} />
          <Route path={routes.routes.userLogin} element={<UserLogin />} />
        </Route>

        <Route
          element={
            <UserProvider>
              <ClientProvider>
                <FiltersInitializer />
                <NotificationsInitializer />
                <UsersMenu />

                <Outlet />
              </ClientProvider>
            </UserProvider>
          }
        >
          <Route index path={routes.routes.root} element={<Root />} />
          <Route element={<RequireUser />}>
            <Route
              path={routes.routes.dashboard(':username')}
              element={
                <>
                  <DashboardInitializer />
                  <FilesInitializer />
                  <ModelsInitializer />
                  <UserDashboard />
                </>
              }
            />
            <Route
              path={routes.routes.models(':username')}
              element={
                <>
                  <ModelsInitializer />
                  <UserModels />
                </>
              }
            />
            <Route
              path={routes.routes.uploads(':username')}
              element={
                <>
                  <UploadsInitializer />
                  <UserUploads />
                </>
              }
            />
            <Route
              path={routes.routes.settings(':username')}
              element={
                <>
                  <SettingsInitializer />
                  <UserSettings />
                </>
              }
            />
            <Route
              path={routes.routes.notifications(':username')}
              element={<UserNotifications />}
            />
            <Route
              path={routes.routes.newModel(':username')}
              element={<CreateModel />}
            />
            <Route
              path={routes.routes.editModel(':username', ':modelId')}
              element={<EditModel />}
            />
            <Route
              path={routes.routes.filters.list(':username')}
              element={<UserFilters />}
            />
            <Route
              path={routes.routes.filters.new(':username')}
              element={<CreateFilter />}
            />
            <Route
              path={routes.routes.filters.edit(':username', ':filterId')}
              element={<EditFilter />}
            />
            <Route
              path={routes.routes.filters.view(':username', ':filterId')}
              element={
                <>
                  <UserFoldersInitializer />
                  <UserFilter />
                </>
              }
            />
            <Route
              path={routes.routes.queryFolders.view(':username', ':filterId')}
              element={<UserQueryFoldersPage />}
            />
            <Route
              path={routes.routes.createDocument(':username', ':modelId')}
              element={<UserDocumentCreate />}
            />
            <Route
              path={routes.routes.editFileStructure(
                ':username',
                ':folderId',
                ':fileId'
              )}
              element={<EditFileStructure />}
            />
            <Route
              path={routes.routes.editFileData(
                ':username',
                ':folderName',
                ':fileName'
              )}
              element={<FileEditPage />}
            />
            <Route path={routes.routes.playground} element={<Playground />} />
          </Route>
          <Route
            path={routes.routes.clientLogin(':folderName')}
            element={<ClientLogin />}
          />
          <Route path={routes.routes.access} element={<ClientLogin />} />
          <Route
            element={
              <>
                <Box my="md">
                  <Center>
                    <LogoSVG height={30} theme={computedColorScheme} />
                  </Center>
                </Box>
                <Outlet />
              </>
            }
          >
            <Route
              path={routes.routes.oldClientFolder(':folderName')}
              element={<RedirectOldClientFolder />}
            />
            <Route
              path={routes.routes.clientFolder(':folderName')}
              element={<FolderPage />}
            />
            <Route
              path={routes.routes.folderFiles(':folderName', ':fileName')}
              element={<FileEditPage />}
            />
          </Route>
        </Route>
        <Route
          path="*"
          element={
            <EmptyStatePage
              heading="Error 404: Page Not Found"
              content="The page you are looking for does not exist. Try checking the URL or visit a different page."
            />
          }
        />
      </Routes>
    </Container>
  );
}

function AppWithProviders() {
  return (
    <Provider store={store}>
      <MantineProvider theme={theme} colorSchemeManager={colorSchemeManager}>
        <Notifications position="bottom-center" />
        <CookieConsentProvider>
          <LocaleProvider>
            <VersionChangeNotifier />
            <CookieConsentBanner />
            <LoadingProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </LoadingProvider>
          </LocaleProvider>
        </CookieConsentProvider>
      </MantineProvider>
    </Provider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

console.log('ROOT_API_URL', process.env.REACT_APP_ROOT_API_URL);
console.log(
  'SHOULD_ADD_DIACRITICS',
  process.env.REACT_APP_SHOULD_ADD_DIACRITICS
);

// Initialize sound leader coordination for cross-tab notification sounds
initializeSoundLeader();

// Initialize version manager - check version and clear localStorage if changed
initializeVersionManager();

root.render(
  // <React.StrictMode>
  // <ThemeProvider>
  <AppWithProviders />
  // </ThemeProvider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
